import React from 'react';
import { Pricing } from '../components/Pricing';
import { Faq } from '../components/Faq';
import { ShieldCheck, Check, Minus } from 'lucide-react';
import { Link } from 'react-router-dom';

export const PricingPage: React.FC = () => {
  return (
    <div className="canvas-dark">
      {/* Header */}
      <header className="page-hero">
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="eyebrow">Transparent Indian Rupee (₹) Pricing</div>
          <h1 className="display-large" style={{ marginTop: 12 }}>
            Fair plans for every stage. <br />
            Zero dark patterns.
          </h1>
          <p className="body-lg" style={{ maxWidth: 720, margin: '14px auto 0 auto' }}>
            Start completely free with up to 50 active users and 100 free SMS every month. Upgrade as your SaaS scales with predictable pricing in INR (₹). No surprise tier cliffs, no captive password hashes.
          </p>
        </div>
      </header>

      {/* 5 Plans, Add-ons & Estimator */}
      <Pricing />

      {/* Comprehensive 5-Tier Specification Matrix */}
      <section className="container" style={{ paddingBottom: 64 }}>
        <div className="section-header" style={{ textAlign: 'center', marginTop: 32 }}>
          <div className="eyebrow">Feature Matrix Specification</div>
          <h2 className="heading-xl" style={{ marginTop: 8 }}>
            Compare all 5 plans side-by-side
          </h2>
          <p style={{ color: '#D7D7D7', fontSize: '0.95rem', maxWidth: 640, margin: '8px auto 0' }}>
            Every feature, limit, and channel rate is explicitly documented below. No hidden fees or surprise billings.
          </p>
        </div>
 
        <p className="matrix-scroll-hint">
          ← Swipe horizontally to inspect all 5 tier specifications →
        </p>
        <div className="matrix-table-container" style={{ marginTop: 16 }}>
          <table className="matrix-table" style={{ width: '100%', minWidth: '850px' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '220px' }}>Feature / Dimension</th>
                <th>Free</th>
                <th>Standard</th>
                <th style={{ background: '#1E2238', color: '#6D7CFF' }}>Pro (Popular)</th>
                <th>Premium</th>
                <th>Enterprise</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Monthly Fee</strong></td>
                <td><strong style={{ color: '#FFFFFF' }}>₹0</strong> / mo</td>
                <td><strong style={{ color: '#FFFFFF' }}>₹799</strong> / mo</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)', fontWeight: 500, color: '#FFFFFF' }}>
                  <strong>₹1,599</strong> / mo
                </td>
                <td><strong style={{ color: '#FFFFFF' }}>₹3,999</strong> / mo</td>
                <td><strong style={{ color: '#FFFFFF' }}>Custom Quote</strong></td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Active Monthly Users Limit</strong></td>
                <td><strong style={{ color: '#10B981' }}>50 Users</strong></td>
                <td>1,000 Users</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)', fontWeight: 500, color: '#FFFFFF' }}>5,000 Users</td>
                <td>15,000 Users</td>
                <td>Unlimited / Custom</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Included Monthly SMS</strong></td>
                <td><strong style={{ color: '#10B981' }}>100 SMS free</strong></td>
                <td>1,000 SMS free</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>3,000 SMS free</td>
                <td>10,000 SMS free</td>
                <td>Custom Pooled</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>SMS Rate (after allowance)</strong></td>
                <td>₹0.99 / SMS</td>
                <td>₹0.99 / SMS</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>₹0.99 / SMS</td>
                <td>₹0.99 / SMS</td>
                <td>BYO Twilio / Wholesale</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>WhatsApp Business OTP Add-on</strong></td>
                <td>₹0.99 / msg</td>
                <td>₹0.99 / msg</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>₹0.99 / msg</td>
                <td>₹0.99 / msg</td>
                <td>BYO Meta Cloud / Wholesale</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Email Auth &amp; Magic Links</strong></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Free &amp; Unlimited</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Free &amp; Unlimited</span></td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Free &amp; Unlimited</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Free &amp; Unlimited</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Free &amp; Unlimited</span></td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>FIDO2 WebAuthn Passkeys</strong></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Included</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Included</span></td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Included</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Included</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Included</span></td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Custom Domain Branding</strong></td>
                <td>Shared Domain</td>
                <td><code>auth.yourdomain.in</code></td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>Multi-Tenant Domains</td>
                <td>Unlimited White-label</td>
                <td>Multi-region custom domains</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Enterprise SAML 2.0 SSO</strong></td>
                <td><Minus size={18} color="#6B7280" /></td>
                <td><Minus size={18} color="#6B7280" /></td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)', color: '#FFFFFF' }}>1 Connection Included</td>
                <td>5 Connections Included</td>
                <td>Unlimited Connections</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Telephony Toll-Fraud Shield</strong></td>
                <td>Basic Rate Limiting</td>
                <td>15m Lockout Defense</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>Automated Circuit Breaker</td>
                <td>Real-time Bot Deterrence</td>
                <td>Dedicated Enterprise WAF</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>Deployment Architecture</strong></td>
                <td>Shared Edge Cluster</td>
                <td>Shared Edge Cluster</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>Shared Edge Cluster</td>
                <td>High-Throughput Cluster</td>
                <td><strong style={{ color: '#FFFFFF' }}>Dedicated VPC or On-Prem K8s</strong></td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>SLA &amp; Support Guarantee</strong></td>
                <td>Community Support</td>
                <td>Email Support (24h)</td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}>Priority Email (4h)</td>
                <td><strong style={{ color: '#FFFFFF' }}>99.99% Edge SLA</strong> + Slack</td>
                <td><strong style={{ color: '#FFFFFF' }}>99.999% SLA</strong> + 24/7 TAM</td>
              </tr>
              <tr>
                <td><strong style={{ color: '#FFFFFF' }}>1-Click Password Hash Export</strong></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Zero Lock-in</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Zero Lock-in</span></td>
                <td style={{ background: 'rgba(109, 124, 255, 0.08)' }}><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Zero Lock-in</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Zero Lock-in</span></td>
                <td><span style={{ color: '#10B981', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Check size={16} /> Automated Cloud Sync</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Anti-Dark-Pattern Guarantee Banner */}
        <div style={{ marginTop: '36px', background: '#1A1A1A', border: '1px solid #374151', borderRadius: '8px', padding: '24px 28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShieldCheck size={20} color="#6D7CFF" />
              <strong style={{ color: '#FFFFFF', fontSize: '1rem' }}>Strict Anti-Dark-Patterns Guarantee</strong>
            </div>
            <p style={{ color: '#D7D7D7', fontSize: '0.88rem', marginTop: '4px', maxWidth: '680px' }}>
              No silent tier upgrades, no mandatory credit card for the Free plan, no hostage password hashes, and instant 1-click self-serve cancellation.
            </p>
          </div>
          <Link to="/terms" className="button-secondary" style={{ fontSize: '13px' }}>
            View Legal SLA →
          </Link>
        </div>

      </section>

      {/* FAQ */}
      <Faq />
    </div>
  );
};

export default PricingPage;
